/**
 * The ambient bed — the landing page's background music. It plays on the landing screen only;
 * the app shell mutes the ambient bus on the consent gate and in the receiver, so the game
 * itself runs without a bed.
 *
 * A real pre-made loop drops into `client/public/audio/ambient/` later — if one of the files in
 * `LOOP_CANDIDATES` exists it is used verbatim. Until then the bed is synthesised: a detuned
 * sub drone, a slow-breathing filtered noise wash, and rare distant "carrier" blips. Procedural
 * costs no download and never loops audibly.
 */
import { audioContext, rampTo } from './context.js';

const LOOP_CANDIDATES = [
  '/audio/ambient/landing.ogg',
  '/audio/ambient/landing.mp3',
  '/audio/ambient/landing.webm',
];

const FADE_SECONDS = 2.5;

export interface AmbientBed {
  output: GainNode;
  setLevel(level: number): void;
  stop(): void;
}

async function loadLoopBuffer(ctx: AudioContext): Promise<AudioBuffer | null> {
  for (const url of LOOP_CANDIDATES) {
    try {
      const response = await fetch(url);
      if (!response.ok) continue;
      const type = response.headers.get('content-type') ?? '';
      // A dev server happily returns index.html for a missing asset — don't try to decode that.
      if (type.includes('text/html')) continue;
      return await ctx.decodeAudioData(await response.arrayBuffer());
    } catch {
      /* try the next candidate */
    }
  }
  return null;
}

function noiseBuffer(ctx: AudioContext, seconds = 4): AudioBuffer {
  const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
  const channel = buffer.getChannelData(0);
  // Brown-ish noise: integrated white noise, gentler and more "room tone" than flat white.
  let last = 0;
  for (let i = 0; i < channel.length; i += 1) {
    const white = Math.random() * 2 - 1;
    last = (last + 0.02 * white) / 1.02;
    channel[i] = last * 3.5;
  }
  return buffer;
}

/**
 * Start the ambient bed. Safe to call before the context is running — it ramps in when it is.
 * `destination` is normally the mixer's `ambient` bus; it falls back to the raw output so the
 * bed can also be used on its own (e.g. before the mixer exists).
 */
export function startAmbientBed(level = 0.35, destination?: AudioNode): AmbientBed {
  const ctx = audioContext();
  const output = ctx.createGain();
  output.gain.value = 0;
  output.connect(destination ?? ctx.destination);

  const disposers: Array<() => void> = [];
  let stopped = false;

  void loadLoopBuffer(ctx).then((buffer) => {
    if (stopped) return;
    if (buffer) {
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      source.connect(output);
      source.start();
      disposers.push(() => source.stop());
    } else {
      buildSynthBed(ctx, output, disposers);
    }
    rampTo(output.gain, level, FADE_SECONDS);
  });

  return {
    output,
    setLevel: (next: number) => rampTo(output.gain, next, 0.3),
    stop: () => {
      if (stopped) return;
      stopped = true;
      rampTo(output.gain, 0, 0.6);
      setTimeout(() => {
        for (const dispose of disposers) {
          try {
            dispose();
          } catch {
            /* already stopped */
          }
        }
        output.disconnect();
      }, 700);
    },
  };
}

function buildSynthBed(ctx: AudioContext, output: GainNode, disposers: Array<() => void>): void {
  // 1. Sub drone — two slightly detuned oscillators beating against each other.
  const droneGain = ctx.createGain();
  droneGain.gain.value = 0.16;
  droneGain.connect(output);

  for (const [frequency, detune] of [
    [55, 0],
    [55, 7],
    [82.5, -5],
  ] as const) {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = frequency;
    osc.detune.value = detune;
    const gain = ctx.createGain();
    gain.gain.value = frequency > 60 ? 0.25 : 0.5;
    osc.connect(gain).connect(droneGain);
    osc.start();
    disposers.push(() => osc.stop());
  }

  // 2. Noise wash — brown noise through a lowpass whose cutoff breathes on a slow LFO.
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer(ctx);
  noise.loop = true;

  const wash = ctx.createBiquadFilter();
  wash.type = 'lowpass';
  wash.frequency.value = 320;
  wash.Q.value = 0.7;

  const washGain = ctx.createGain();
  washGain.gain.value = 0.5;

  noise.connect(wash).connect(washGain).connect(output);
  noise.start();
  disposers.push(() => noise.stop());

  const lfo = ctx.createOscillator();
  lfo.type = 'sine';
  lfo.frequency.value = 0.05; // one breath every ~20s
  const lfoDepth = ctx.createGain();
  lfoDepth.gain.value = 160;
  lfo.connect(lfoDepth).connect(wash.frequency);
  lfo.start();
  disposers.push(() => lfo.stop());

  // 3. Distant carrier blips — rare, quiet, always slightly off in the distance.
  let timer: ReturnType<typeof setTimeout> | null = null;
  const scheduleBlip = (): void => {
    timer = setTimeout(
      () => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const now = ctx.currentTime;
        osc.type = 'sine';
        osc.frequency.value = 400 + Math.random() * 900;
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.03, now + 0.04);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
        osc.connect(gain).connect(output);
        osc.start(now);
        osc.stop(now + 0.4);
        scheduleBlip();
      },
      6000 + Math.random() * 14000,
    );
  };
  scheduleBlip();
  disposers.push(() => {
    if (timer) clearTimeout(timer);
  });
}
